export type ValidatorError = {
  message: string;
};

export type ValidatorFn<T> = (value: T | null) => ValidatorError | null;

// Compile regex once at module level
const EMAIL_REGEX = /^.+@.+$/;
// TODO: require some equivalent of validate
export abstract class AsyncValidatorFn<T> {
  constructor() {}
  public abstract validate: (value: T | null) => Promise<ValidatorError | null>;
  public abstract onOptionChange: (options: Promise<Option[]>) => void;
}

export const TextRequiredValidator: (name: string) => ValidatorFn<string> =
  (name: string) => (value: string | null) => {
    // Treat null, empty string, or "0" as empty (0 is often the default "no selection" value)
    return value != null && value.trim().length > 0 && value.trim() !== "0"
      ? null
      : {
          message: `${name} is required`,
        };
  };

export const EmailValidator: ValidatorFn<string> = (value: string | null) => {
  if (!value || value.trim().length === 0) {
    return null; // Let Required validator handle empty values
  }

  // Use pre-compiled regex
  return EMAIL_REGEX.test(value.trim())
    ? null
    : {
        message: "Please enter a valid email address",
      };
};

export interface Option {
  value: any | null;
  display: string;
}

// value here is the raw string in the input
export const ValidOptionSelectedValidator =
  (options: Option[]) => (value: string | null) => {
    // Skip validation for empty values - let Required validator handle those
    if (!value || value.trim() === "" || value === "0") {
      return null;
    }

    return options.map((o) => o.value?.toString() ?? null).includes(value)
      ? null
      : {
          message: `${value} is not valid`,
        };
  };

export class AsyncValidOptionSelectedValidator extends AsyncValidatorFn<string> {
  public options: Promise<Option[]> = new Promise(() => []);
  public validate = (value: string | null): Promise<ValidatorError | null> => {
    if (!value) return new Promise((res) => res(null));
    // once all the options are loaded, check that the current value is an option
    return this.options.then((opts) => {
      // TODO: this shouldn't use the display, it should use the value
      // fix in public.ts then correct this
      return opts.map((o) => o.display?.toString() ?? null).includes(value)
        ? null
        : {
            message: `${value} is not valid`,
          };
    });
  };

  public onOptionChange = (options: Promise<Option[]>) => {
    this.options = options;
  };
}

// Validator for multiselect that checks both required state and selection state
export const MultiselectRequiredValidator =
  (
    name: string,
    getHiddenInputValue: () => string,
    hiddenInput: HTMLInputElement,
  ) =>
  (value: string | null) => {
    const hiddenValue = getHiddenInputValue();
    const hasSelections = hiddenValue && hiddenValue.trim().length > 0;
    const visibleValue = value ? value.trim() : "";

    if (!hasSelections && visibleValue.length === 0) {
      // Required field with no selections and empty visible input
      return {
        message: `${name} is required`,
      };
    }

    if (visibleValue.length > 0) {
      // Has text in visible input - must select from dropdown or clear
      return {
        message: `Please select an option from the dropdown options or clear the field`,
      };
    }

    // Field is valid - has selections and empty visible input
    return null;
  };

// TODO: set up form and form controls to keep data
// connect validators to it
// type magic?
